import moment, { Moment } from 'moment-timezone';
import { EmployeeRole } from '@/types';
import {
  findAvailabilityDay,
  OrderAvailability,
} from '@domain/fulfillment-settings/blocs/order-availability';
import {
  getValidTimeslotForDate,
  isFulfillmentTypeAvailable,
} from '@/experiences/sales-portal/blocs/utils/fulfillment-utils';
import {
  SavedSelectedFulfillment,
  selectRestaurantFulfillment,
  useSelectedFulfillmentStore,
} from '@/experiences/sales-portal/domain/ordering/blocs/stores/selected-fulfillment-store';
import { TimeZoneMap } from '@domain/fulfillment-settings/types/types';
import { useCallback, useEffect, useMemo } from 'react';
import { useSalesPortalAvailableOrderFulfillmentsLoader } from '@/experiences/sales-portal/domain/ordering/blocs/loaders/use-sales-portal-available-order-fulfillments-loader';

/**
 * Hook to get and save sales portal fulfillments with proper date and timeslot validation.
 *
 * Key differences from operator portal (useSelectedFulfillment):
 * - No draft order handling: Sales portal uses local cart store, not draft orders
 * - Simplified hydration: No draft order case, only saved fulfillment cases
 * - MMKV storage via Zustand instead of AsyncStorage
 *
 * Admin bypass: Users with ADMIN or SUPPLIER_DEFAULT roles (or roles in
 * additionalOrderValidationRoles config) can bypass fulfillment restrictions
 * like blackout dates and cutoff times.
 *
 * @param supplierUUID - The UUID of the supplier
 * @param restaurantUUID - The UUID of the restaurant
 * @param restaurantTimezone - The restaurant's timezone (IANA format)
 * @param supplierTimezone - The supplier's timezone (IANA format)
 * @param employeeRole - The employee role of the logged-in user for the supplier
 * @param additionalOrderValidationRoles - Additional roles from dynamic config that can bypass restrictions
 *
 * @returns Object containing fulfillment data, validation callbacks, and state management
 */
export const useSalesPortalFulfillments = (
  supplierUUID: string,
  restaurantUUID: string,
  restaurantTimezone: string,
  supplierTimezone: string,
  employeeRole: EmployeeRole | null,
  additionalOrderValidationRoles: EmployeeRole[],
) => {
  const savedSelectedFulfillment = useSelectedFulfillmentStore((state) =>
    selectRestaurantFulfillment(state, restaurantUUID),
  );

  const {
    availableFulfillments,
    getAvailabilityConstraints,
    loading: fulfillmentsLoading,
    refetch,
    selectableFulfillmentOptions,
  } = useSalesPortalAvailableOrderFulfillmentsLoader({
    restaurantUUID,
    supplierUUID,
  });

  const timezones: TimeZoneMap = useMemo(
    () => ({ restaurant: restaurantTimezone, supplier: supplierTimezone }),
    [restaurantTimezone, supplierTimezone],
  );

  // Computed: check if available fulfillments is empty (for error state handling)
  const emptyFulfillments = useMemo(
    () => !fulfillmentsLoading && availableFulfillments.length === 0,
    [availableFulfillments.length, fulfillmentsLoading],
  );

  const saveSelectedFulfillment = useCallback(
    (fulfillment: SavedSelectedFulfillment) => {
      useSelectedFulfillmentStore
        .getState()
        .saveSelectedFulfillment(restaurantUUID, fulfillment);
    },
    [restaurantUUID],
  );

  /**
   * Create OrderAvailability instance for the current fulfillment type.
   * Falls back to first available type if saved type is no longer available.
   */
  const orderAvailability = useMemo(() => {
    if (availableFulfillments.length === 0) {
      return null;
    }

    // check if saved type is still available, otherwise use first available
    const savedType = savedSelectedFulfillment?.type;
    const isTypeAvailable =
      savedType && isFulfillmentTypeAvailable(savedType, availableFulfillments);

    const fulfillmentType = isTypeAvailable
      ? savedType
      : (availableFulfillments[0]?.fulfillment_name ?? null);

    return new OrderAvailability(
      availableFulfillments,
      timezones,
      fulfillmentType,
    );
  }, [availableFulfillments, savedSelectedFulfillment?.type, timezones]);

  /**
   * Get timeslot options for a specific date.
   * Returns array of available timeslots with disabled state.
   */
  const getTimeslotOptionsForDate = useCallback(
    (date: Moment) => {
      if (!orderAvailability) {
        return [];
      }

      const availabilityDay = findAvailabilityDay(
        orderAvailability.selectedAvailability,
        date,
      );
      if (!availabilityDay) {
        return [];
      }

      return orderAvailability.getTimeslotOptionsForDay(
        availabilityDay,
        employeeRole,
        additionalOrderValidationRoles,
      );
    },
    [additionalOrderValidationRoles, orderAvailability, employeeRole],
  );

  /**
   * Hydration logic with proper date, type, and timeslot validation.
   *
   * 5-Case Logic (matching operator portal pattern):
   * 0. No available fulfillments - fallback to Delivery + tomorrow (allows switching restaurants)
   * 1. Valid saved fulfillment (type + date + timeslot) - keep it, validate timeslot
   * 2. Invalid saved type (no longer available) - switch to first available type + next date + default timeslot
   * 3. Invalid saved date (type valid but date invalid) - keep type, update to next available date + default timeslot
   * 4. No saved fulfillment - default to first available type + next available date + default timeslot
   *
   * IMPORTANT: This effect intentionally does NOT include savedSelectedFulfillment in deps.
   * Including it would create a feedback loop where user selections are overwritten.
   * The effect only runs on initial load, restaurant switch, or fulfillments change.
   */
  useEffect(() => {
    // Case 0: No available fulfillments - fallback to Delivery + tomorrow
    // This prevents infinite loading and allows users to switch restaurants
    if (availableFulfillments.length === 0 && !fulfillmentsLoading) {
      const fallbackOrderAvailability = new OrderAvailability(
        availableFulfillments,
        timezones,
        null,
      );
      saveSelectedFulfillment({
        date: fallbackOrderAvailability.nextFulfillmentDayMoment.toISOString(),
        fulfillmentSettingsUUID: null,
        timeslot: null,
        type: fallbackOrderAvailability.fulfillmentType,
      });
      return;
    }

    if (availableFulfillments.length === 0) {
      return;
    }

    const firstFulfillment = availableFulfillments[0];

    // Case 1, 2 & 3: Check saved fulfillment
    if (savedSelectedFulfillment) {
      const isTypeAvailable = isFulfillmentTypeAvailable(
        savedSelectedFulfillment.type,
        availableFulfillments,
      );

      // Case 2: Saved type no longer available - switch to first available
      if (!isTypeAvailable) {
        const defaultValidator = new OrderAvailability(
          availableFulfillments,
          timezones,
          firstFulfillment.fulfillment_name,
        );

        const nextDate = defaultValidator.nextFulfillmentDayMoment;
        const nextTimeslot = getValidTimeslotForDate(
          firstFulfillment,
          nextDate,
          null,
        );

        saveSelectedFulfillment({
          date: nextDate.toISOString(),
          fulfillmentSettingsUUID:
            firstFulfillment.fulfillment_settings_id || null,
          timeslot: nextTimeslot,
          type: firstFulfillment.fulfillment_name,
        });
        return;
      }

      // Type is valid - check date
      const validator = new OrderAvailability(
        availableFulfillments,
        timezones,
        savedSelectedFulfillment.type,
      );

      const currentFulfillment = availableFulfillments.find(
        (f) => f.fulfillment_name === savedSelectedFulfillment.type,
      );

      const dateMoment = moment(savedSelectedFulfillment.date).tz(
        restaurantTimezone,
      );
      const isDateValid = validator.isValidSupplierFulfillmentDate(
        dateMoment,
        employeeRole,
        additionalOrderValidationRoles,
      );

      if (isDateValid) {
        // Case 1: Valid saved fulfillment - validate timeslot only
        if (currentFulfillment) {
          const validatedTimeslot = getValidTimeslotForDate(
            currentFulfillment,
            dateMoment,
            savedSelectedFulfillment.timeslot,
          );

          // only update if timeslot changed
          const timeslotChanged =
            validatedTimeslot?.startTime !==
              savedSelectedFulfillment.timeslot?.startTime ||
            validatedTimeslot?.endTime !==
              savedSelectedFulfillment.timeslot?.endTime;

          if (timeslotChanged) {
            saveSelectedFulfillment({
              ...savedSelectedFulfillment,
              timeslot: validatedTimeslot,
            });
          }
        }
        return;
      }

      // Case 3: Invalid date - update to next available, keep type
      const nextDate = validator.nextFulfillmentDayMoment;
      const nextTimeslot = currentFulfillment
        ? getValidTimeslotForDate(
            currentFulfillment,
            nextDate,
            savedSelectedFulfillment.timeslot,
          )
        : null;

      saveSelectedFulfillment({
        date: nextDate.toISOString(),
        fulfillmentSettingsUUID: validator.selectedFulfillmentSettingsUUID,
        timeslot: nextTimeslot,
        type: savedSelectedFulfillment.type,
      });
      return;
    }

    // Case 4: No saved fulfillment - default to first available
    if (!firstFulfillment) {
      return;
    }

    const defaultValidator = new OrderAvailability(
      availableFulfillments,
      timezones,
      firstFulfillment.fulfillment_name,
    );

    const nextDate = defaultValidator.nextFulfillmentDayMoment;
    const nextTimeslot = getValidTimeslotForDate(
      firstFulfillment,
      nextDate,
      null,
    );

    saveSelectedFulfillment({
      date: nextDate.toISOString(),
      fulfillmentSettingsUUID: firstFulfillment.fulfillment_settings_id || null,
      timeslot: nextTimeslot,
      type: firstFulfillment.fulfillment_name,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    additionalOrderValidationRoles,
    restaurantUUID,
    availableFulfillments.length,
    fulfillmentsLoading,
    employeeRole,
    timezones,
  ]);

  return {
    availableFulfillments,
    emptyFulfillments,
    fulfillmentsLoading,
    getAvailabilityConstraints,
    getTimeslotOptionsForDate,
    orderAvailability,
    refetch,
    savedSelectedFulfillment,
    saveSelectedFulfillment,
    selectableFulfillmentOptions,
    timezones,
  };
};
